import { useMemo } from "react";
import useGameStore from "@store/useGameStore";
import useSelectionStore from "@store/useSelectionStore";
import { ClickDTO } from '@models/ClickDTO';
import { ClickableType } from '@utils/enums';
import UnitCard from "@components/ui/game/unit-card/UnitCard";
import HorizontalSlider from "@components/ui/HorizontalSlider/HorizontalSlider";
import "./DeployableUnitsPanel.css";

/**
 * DeployableUnitsPanel - Shows deployable units with HorizontalSlider
 * Consistent with DeployedUnitsTab and CinematicPanel
 */
export default function DeployableUnitsPanel() {
  const currentClick = useSelectionStore((state) => state.currentClick);
  const handleClick = useSelectionStore((state) => state.handleClick);
  const selectedUnitId = currentClick?.data?.id;

  // Subscribe to game status to trigger recalculation when phase changes
  const gameStatus = useGameStore((state) => state.game?.status);

  // Get deployable units - recalculates when game status changes
  const deployableUnits = useMemo(() => {
    try {
      return useGameStore.getState().getDeployableUnits();
    } catch (error) {
      console.error('[DeployableUnitsPanel] Error getting deployable units:', error);
      return [];
    }
  }, [gameStatus]);

  // Get deployed units to filter
  const deployedUnits = useGameStore((state) => state.getCurrentUnits());

  // Filter out already deployed units
  const availableUnits = useMemo(() => {
    const deployedIds = new Set(deployedUnits.map(u => u.id));
    return deployableUnits.filter(unit => !deployedIds.has(unit.id));
  }, [deployableUnits, deployedUnits]);

  // Click handler
  const handleUnitClick = (unit) => {
    const clickDTO = new ClickDTO(ClickableType.DEPLOYABLE, unit, 'DeployableUnitsPanel');
    handleClick(clickDTO);
  };

  // Empty state
  if (availableUnits.length === 0) {
    return (
      <div className="footer-panel-container footer-panel-content">
        <div className="panel-empty-state">
          <p>No units available to deploy</p>
        </div>
      </div>
    );
  }

  return (
    <div className="footer-panel-container footer-panel-content">
      <HorizontalSlider className="card-track">
        {availableUnits.map((unit) => {
          const isSelected = selectedUnitId === unit.id;
          return (
            <UnitCard
              key={unit.id}
              unit={unit}
              size="medium"
              isSelected={isSelected}
              teamClass="own"
              showEnergyCost
              onClick={() => handleUnitClick(unit)}
            />
          );
        })}
      </HorizontalSlider>
    </div>
  );
}
